import { otto } from "@gr-exports/async/nexus";
import { defineStore, defineEvent } from "@gr-common/async/nexus";
import { now } from "@gr-common/async/date";
import type { NavigationRaw } from "../index.js";
import type {
  NavigationScope,
  NavigationScopeAsync,
  NavigationScopeName,
  NavigationScopeOptions,
  NavigationScopeState,
} from "./scope.types.js";
import { asyncScope, NAV_META, NAV_SCOPES } from "../lib/utils.js";
import { getCurrentNavigation } from "../lib/navigation.js";
import type { NavigationState, StateManaged, StateRecord } from "../data/state.types.js";
import { locationState, sanitizeState } from "../data/state.js";
import { defineContext, historyPatched } from "../lib/history.js";
import type { NavigationParameters, ParameterRecord } from "../data/params.types.js";
import type { AsyncNamespace } from "../../nexus/namespace/namespace.types.js";
import type { NexusHandler } from "../../nexus/function/function.types.js";
import { navigate } from "../lib/navigate.js";
import { hashFiltered, pathFiltered, scopeCache, scopesFiltered, searchFiltered } from "./cache.js";
import { locationToParams, paramTypes } from "../data/params.js";

const log = asyncScope.scope("scope");

/**
 *
 *
 *
 *
 *
 *
 */
export const getScopedState = /*           */ (
  scope: string,
  state: StateManaged,
): NavigationState<StateRecord> => locationState(state[NAV_SCOPES][scope]);

export const setScope = /*           */ (
  scope: string,
  state: StateManaged,
  value: StateRecord,
): void => {
  state[NAV_META][scope] = now();
  state[NAV_SCOPES][scope] = value;
};

/**
 *
 *
 *
 */
const ensureEntry = /*           */ <T>(map: Map<string, Set<T>>, key: string): Set<T> => {
  if (!map.has(key)) map.set(key, new Set());
  return map.get(key)!;
};

const ensureRegExpEntry = /*           */ <T>(
  map: Map<RegExp, Set<T>>,
  pattern: RegExp | undefined,
): Set<T> | undefined => {
  if (!pattern) return;
  const key = new RegExp(pattern);
  if (Array.from(map.keys()).every((k) => k.toString() !== key.toString())) map.set(key, new Set());
  return map.get(key)!;
};

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export const initializeNamespaceWithCurrentNavigation = /*           */ (
  namespace: AsyncNamespace<NavigationScopeAsync>,
  state: NavigationState<StateRecord>,
  hash: NavigationParameters<ParameterRecord>,
  search: NavigationParameters<ParameterRecord>,
): void => {
  /*                                                         */
  if (!namespace.isReady) {
    namespace.assignNamespace({
      current: defineStore<NavigationScopeState<NavigationScopeOptions>>({
        url: new URL(location.href),
        state,
        hash,
        search,
        type: "push",
      }),
      currentHash: defineStore(hash),
      currentSearch: defineStore(search),
      currentState: defineStore(state),
      onNavigate: defineEvent(),
    });
  }
};

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
const mergeParams = (
  managed: NavigationParameters<ParameterRecord>,
  fromLocation: string,
  nextParams: URLSearchParams,
): string =>
  decodeURI(
    new URLSearchParams(
      managed.toEntries(fromLocation).reduce(
        (acc, entry) => {
          if (!acc.find(([key]) => entry[0] === key)) acc.push(entry as string[]);
          return acc;
        },
        [...nextParams] as string[][],
      ),
    ).toString(),
  );

/**
 *
 *
 *
 */
export const scope = (<N extends NavigationScopeName, O extends NavigationScopeOptions>(
  name: N,
  options: O = {} as O,
) => {
  /*                                                                                       */
  const { hash: hashOptions = [], search: searchOptions = [] } = options;
  const pathPattern = options.path ? new RegExp(options.path) : undefined;

  const scopeId = `${name}:${hashOptions.join("|")}:${searchOptions.join("|")}:${pathPattern?.toString()}`;
  log.info("Aquiring scope", scopeId, "with options", options);

  /*                                              */
  if (scopeCache.has(scopeId)) return scopeCache.get(scopeId)!;

  /*                                              */
  const namespace = otto.navigation[NAV_SCOPES][scopeId];

  /*                                                         */
  const [, state, hash, search] = getCurrentNavigation(undefined, hashOptions, searchOptions);
  const scopeState = getScopedState(name, state);
  initializeNamespaceWithCurrentNavigation(
    namespace,
    scopeState.clone(),
    hash.clone(),
    search.clone(),
  );

  /*                     */
  const res: NavigationScope<string, NavigationScopeOptions> = {
    name,
    key: scopeId,
    current: namespace.current,
    currentHash: namespace.currentHash,
    currentSearch: namespace.currentSearch,
    currentState: namespace.currentState,
    onNavigate: namespace.onNavigate,

    hash(params) {
      if (params) hash.fromObject(params);
      return hash as never;
    },

    search(params) {
      if (params) search.fromObject(params);
      return search as never;
    },

    state(params) {
      if (params) scopeState.fromObject(params as never);
      return scopeState as never;
    },

    async navigate(urlOrString = "", o = {}) {
      const { history: navigationType = "push" } = o;
      log.debug("Navigation request:", navigationType, "for:", urlOrString, "with options", o);

      /*                                                               */
      if (o.state) scopeState.fromObject(o.state);

      /*                                                      */
      /*                                               */
      const nextUrl = new URL(urlOrString, location.href);

      /*                            */
      nextUrl[paramTypes[1]] = mergeParams(
        hash,
        location[paramTypes[1]],
        locationToParams(nextUrl[paramTypes[1]], paramTypes[1])[1],
      );
      /*                              */
      nextUrl[paramTypes[0]] = mergeParams(search, location[paramTypes[0]], nextUrl.searchParams);

      /*                            */
      const stateManaged = sanitizeState(o.clear ? {} : historyPatched.state);
      setScope(name, stateManaged, scopeState.toObject());

      /*                                                                     */
      /*                                                                                           */
      log.info("Navigate to", nextUrl.href, "as", navigationType, "with state", stateManaged);
      return navigate(nextUrl, { ...o, scope: name, state: stateManaged });
    },

    push: (o, url) => res.navigate(url, { ...o, history: "push" }),
    replace: (o, url) => res.navigate(url, { ...o, history: "replace" }),

    back: (o) => historyPatched.back.call(defineContext({ ...o, scope: name })) ?? true,
    forward: (o) => historyPatched.forward.call(defineContext({ ...o, scope: name })) ?? true,

    reload: () => location.reload() ?? true,
    go: (delta, o) => historyPatched.go.call(defineContext({ ...o, scope: name }), delta) ?? true,
  };

  /*                                           */
  scopeCache.set(scopeId, res);

  /*                                      */
  ensureEntry(scopesFiltered, name).add(res);
  ensureRegExpEntry(pathFiltered, pathPattern)?.add(res);
  hashOptions.forEach((ho) => ensureEntry(hashFiltered, ho).add(res));
  searchOptions.forEach((so) => ensureEntry(searchFiltered, so).add(res));

  return res;
}) satisfies NexusHandler<NavigationRaw["scope"]>;
